/** Shared styles and helpers for feature views */

/**
 * Slugify a title for use as a filename. Strips invalid filesystem characters
 * and caps the length so the OS doesn't reject the resulting name.
 */
export function slugifyFilename(title: string, ext: string): string {
  const cleaned = (title || 'untitled')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80) || 'untitled'
  const dotExt = ext ? (ext.startsWith('.') ? ext : `.${ext}`) : ''
  return `${cleaned}${dotExt}`
}

/** Map a MIME type to a file extension. Returns empty string when unknown. */
export function extensionForMime(mime?: string | null): string {
  if (!mime) return ''
  const m = mime.split(';')[0].trim().toLowerCase()
  switch (m) {
    case 'image/png': return 'png'
    case 'image/jpeg':
    case 'image/jpg': return 'jpg'
    case 'image/webp': return 'webp'
    case 'image/gif': return 'gif'
    case 'image/svg+xml': return 'svg'
    case 'audio/mpeg':
    case 'audio/mp3': return 'mp3'
    case 'audio/mp4':
    case 'audio/m4a':
    case 'audio/x-m4a': return 'm4a'
    case 'audio/wav':
    case 'audio/x-wav':
    case 'audio/wave': return 'wav'
    case 'audio/webm': return 'webm'
    case 'audio/ogg': return 'ogg'
    case 'application/pdf': return 'pdf'
    case 'text/html': return 'html'
    case 'text/markdown': return 'md'
    case 'text/plain': return 'txt'
    default: {
      // Last-ditch fallback: take the subtype.
      const sub = m.split('/')[1] || ''
      return sub.replace(/[^a-z0-9]+/g, '').slice(0, 5)
    }
  }
}

/** Trigger a browser download for an existing URL (blob URL or http URL). */
export function triggerDownloadFromUrl(url: string, filename: string) {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/** Trigger a browser download for in-memory text content. */
export function triggerDownloadText(content: string, mimeType: string, filename: string) {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  triggerDownloadFromUrl(url, filename)
  // Revoke after the click handler has had a chance to run.
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}

export function EmptyState({ label }: { label: string }) {
  return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>{label}</div>
}

export function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
      <strong style={{ color: 'var(--text-muted)' }}>{label}:</strong> {value}
    </div>
  )
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/**
 * Serialize as local time without a zone suffix, matching Dart's
 * DateTime.toIso8601String() on a local DateTime. Writing UTC ("...Z") makes
 * Flutter parse a UTC DateTime whose .day/.hour are off by the zone offset.
 */
export function toLocalIso(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:00.000`
}

/** Local YYYY-MM-DD of a stored date string (works for both naive and "Z" strings) */
export function localDatePart(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/** Local HH:MM of a stored date string */
export function localTimePart(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '' : `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/** Local YYYY-MM-DD key of a Date (toISOString() would give the UTC day) */
export function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

export function formatDate(dateStr?: string | null): string {
  if (!dateStr) return ''
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch {
    return dateStr
  }
}

export const cardStyle: Record<string, string | number> = {
  padding: '10px 12px',
  marginBottom: 6,
  background: 'var(--bg-surface)',
  borderRadius: 6,
  fontSize: 14,
}

export const sidebarStyle: Record<string, string | number> = {
  width: 220,
  background: 'var(--bg-surface)',
  borderRight: '1px solid var(--border)',
  overflow: 'auto',
  flexShrink: 0,
}

export const detailPanelStyle: Record<string, string | number> = {
  width: 360,
  background: 'var(--bg-surface)',
  borderLeft: '1px solid var(--border)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  flexShrink: 0,
}

export const listAreaStyle: Record<string, string | number> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minWidth: 0,
}

export const refreshBtnStyle: Record<string, string | number> = {
  padding: '4px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  fontSize: 12,
}

export const filterBtnStyle = (active: boolean): Record<string, string | number> => ({
  padding: '6px 12px',
  border: 'none',
  borderRadius: 6,
  background: active ? 'var(--assistant-bubble)' : 'transparent',
  color: active ? 'var(--text)' : 'var(--text-secondary)',
  cursor: 'pointer',
  fontSize: 13,
})

export const tabStyle = (active: boolean): Record<string, string | number> => ({
  padding: '8px 16px',
  border: 'none',
  background: 'transparent',
  color: active ? 'var(--text)' : 'var(--text-muted)',
  borderBottom: active ? '2px solid var(--primary-hover)' : '2px solid transparent',
  cursor: 'pointer',
  fontSize: 13,
})
